import * as React from 'react';
import dompurify from 'dompurify';
import { useEditor, EditorContent } from '@tiptap/react';
import Bold from '@tiptap/extension-bold';
import Blockquote from '@tiptap/extension-blockquote';
import Italic from '@tiptap/extension-italic';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Document from '@tiptap/extension-document';
import BulletList from '@tiptap/extension-bullet-list';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Link from '@tiptap/extension-link';
import Heading from '@tiptap/extension-heading';
import History from '@tiptap/extension-history';
import HardBreak from '@tiptap/extension-hard-break';
import { GDSInputStatus, StackLayout } from '@leagueplatform/genesis-core';
import { AllowedUriProtocols } from '@web-config-app/core';
import { RichTextEditorToolbar } from './rich-text-editor-toolbar/rich-text-editor-toolbar.component';
import { richTextEditorCss } from './rich-text-editor.css';
import { removeParagraphTagsInsideLi } from './utils/remove-paragraph-tag-inside-li.util';
import { createValidatedLinkExtension } from './utils/link-validation-extension.util';
import { NewlineImportNormalizer } from './utils/newline-import-normalizer.extension';

// Use <b> instead of <strong>
const CustomBold = Bold.extend({
  renderHTML({ HTMLAttributes }) {
    return ['b', HTMLAttributes, 0];
  },
});
// use <i> tag instead of <em>
const CustomItalic = Italic.extend({
  renderHTML({ HTMLAttributes }) {
    return ['i', HTMLAttributes, 0];
  },
});

export const tipTapExtensions = [
  CustomBold,
  Blockquote,
  Paragraph,
  Text,
  Document,
  BulletList,
  ListItem,
  OrderedList,
  CustomItalic,
  Heading,
  History,
  HardBreak,
  NewlineImportNormalizer,
];

interface RichTextEditorProps {
  value: string;
  id: string;
  readOnly?: boolean;
  inputStatus?: GDSInputStatus;
  editorCacheKey?: string;
  allowedUriProtocols?: AllowedUriProtocols;
  onChange: (newValue: string) => void;
  currentLocale?: string;
  hasMultipleLocales?: boolean;
}

const useEditorConfig = ({
  editable,
  id,
  inputStatus,
  onChange,
  value,
  editorCacheKey,
  allowedUriProtocols,
}: Omit<RichTextEditorProps, 'readOnly'> & {
  editable: boolean;
}) => {
  const editorProps = {
    attributes: {
      id,
      as: 'textarea',
      'data-testid': 'rich-text-editor',
      class: `${
        inputStatus === 'error'
          ? 'rich-text-editor-input error'
          : 'rich-text-editor-input'
      }`,
    },
  };

  const editorConfig = useEditor(
    {
      extensions: [
        ...tipTapExtensions,
        allowedUriProtocols
          ? createValidatedLinkExtension(allowedUriProtocols)
          : Link,
      ],
      editable,
      content: value,
      editorProps,
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        const formattedHtml = removeParagraphTagsInsideLi(html);
        // Allow unknown protocols so DOMPurify doesn't strip links with unsupported schemes during editing.
        // This keeps them visible/editable for authors. We still flag them via our allowlist
        // (adds 'link-invalid' class) and validation enforces the policy.
        const sanitizedHtml = dompurify.sanitize(formattedHtml, {
          ALLOW_UNKNOWN_PROTOCOLS: true,
        });
        onChange(sanitizedHtml);
      },
      parseOptions: {
        preserveWhitespace: 'full',
      },
    },
    [editorCacheKey, allowedUriProtocols],
  );

  return editorConfig;
};

export const RichTextEditor = ({
  value = '',
  id,
  readOnly = false,
  onChange,
  inputStatus,
  editorCacheKey,
  currentLocale,
  hasMultipleLocales,
  allowedUriProtocols,
}: RichTextEditorProps) => {
  const editorConfig = useEditorConfig({
    editable: !readOnly,
    id,
    onChange,
    inputStatus,
    value,
    editorCacheKey,
    allowedUriProtocols,
  });

  return (
    <StackLayout css={richTextEditorCss} horizontalAlignment="stretch">
      <RichTextEditorToolbar
        editor={editorConfig}
        readOnly={readOnly}
        currentLocale={currentLocale}
        hasMultipleLocales={hasMultipleLocales}
        allowedUriProtocols={allowedUriProtocols}
      />
      <EditorContent editor={editorConfig} />
    </StackLayout>
  );
};
